/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { MutationCache, QueryClient } from '@tanstack/react-query';
import { ApiError } from './api';
import { showBanner } from '@/app/StatusBanner';
import { sessionKeys } from '@/queries/session';

/**
 * The refresh policy, which is the vanilla app's behaviour restated.
 *
 * `app.js` polls every 20 seconds and skips the poll when the tab is hidden or a
 * modal is open. Only the first of those guards is reproduced, and the second is
 * deliberately **not**:
 *
 *  - **hidden tab** → `refetchIntervalInBackground: false`. This is React
 *    Query's default; it is stated here so nobody "fixes" it later.
 *  - **open modal** → nothing. The guard exists in `app.js` because `render()`
 *    replaces `#view-container` wholesale, so a refresh mid-edit destroyed the
 *    inputs somebody was typing into. React has no such step: the modal seeds its
 *    draft from the task **once per id** (`useEffect(…, [task.id])`), so a
 *    refetch updates the cache underneath it and leaves the draft alone.
 *
 *    Reproducing the guard anyway would be worse than useless — it would mean a
 *    board that stops updating whenever anybody leaves a task open, which is most
 *    of the working day. **Porting a workaround without its cause is how a
 *    rewrite inherits the old design's limits.**
 *
 * The trap this file cannot prevent on its own, from plan §7.5: a component must
 * branch on `isLoading` (no data yet) and never on `isFetching` (data exists,
 * refreshing). Using `isFetching` makes the whole board flash every 20 seconds.
 * The vanilla app gets this right by accident because it never had a loading
 * state to show.
 */
export const AUTO_REFRESH_MS = 20_000;

export const queryClient = new QueryClient({
  /**
   * Every failed write says so, once, in one place.
   *
   * Here rather than in each mutation's `onError` for the reason the vanilla app
   * learned the hard way: a per-call handler is something you can forget, and the
   * ones people forget are the rare paths — exactly the failures worth reporting.
   * A mutation that wants different wording still overrides its own `onError`;
   * this is the floor, not a ceiling.
   *
   * 401 is excluded: the session ended, `<RequireSession>` is already routing to
   * /login, and "Not authenticated" on the way out explains nothing.
   */
  mutationCache: new MutationCache({
    onError: (error) => {
      /**
       * A 401 gets no banner — but it must not get *silence* either.
       *
       * `<RequireSession>` routes to /login when the session query 401s, and the
       * comment in `api.ts` used to claim that covered this. It does not: a
       * failed **write** never makes `/auth/me` re-run, so the session query sat
       * on its last good answer and nothing happened at all. The person pressed
       * Save on an expired session, saw no message, no redirect and no saved
       * work, and had every reason to believe it went through.
       *
       * Found by expiring a token mid-edit and watching. The vanilla app does
       * `window.location.href = '/auth/sso/login'` at this point; asking the
       * session query again is the same answer without discarding the router's
       * record of where the person was.
       */
      if (error instanceof ApiError && error.isUnauthenticated) {
        void queryClient.invalidateQueries({ queryKey: sessionKeys.me });
        return;
      }
      showBanner(error instanceof Error ? error.message : 'Something went wrong');
    },
  }),
  defaultOptions: {
    queries: {
      refetchInterval: AUTO_REFRESH_MS,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: true,
      staleTime: 5_000,
      // A 401 means the session ended — retrying cannot help and only delays the
      // redirect to /login. A 404 from a scoped read is an answer, not a fault.
      retry: (failureCount, error) => {
        if (error instanceof ApiError && (error.isUnauthenticated || error.isMissing)) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});
